/**
 * Feature Flags System
 *
 * Controls access to beta features based on user's resource ID.
 * Resource ID is stored in localStorage when user selects themselves in the tech dropdown.
 */

// Beta users who can see unreleased features (AutoTask Resource IDs)
// Add resource IDs here to grant beta access
const BETA_USER_RESOURCE_IDS: number[] = [
  // Jeremiah Goode - add your resource ID here after checking
  // You can find it by selecting yourself in the tech dropdown, then running:
  // localStorage.getItem('autotask-hub-resource-id') in browser console
];

// Feature definitions
export type FeatureFlag =
  | 'calendar'           // Time entry calendar
  | 'recurring-entries'  // Recurring time entries
  | 'reports-v2'         // New reports system
  | 'billing-periods';   // Billing period lock

// Which features are enabled for beta users
// NOTE: 'calendar' removed from beta - now available to everyone
// NOTE: 'recurring-entries' removed - now available to everyone
const BETA_FEATURES: FeatureFlag[] = [
  'reports-v2',
  'billing-periods',
];

/**
 * Get the current user's resource ID from localStorage
 */
export function getCurrentResourceId(): number | null {
  const stored = localStorage.getItem('autotask-hub-resource-id');
  if (!stored) return null;
  const id = parseInt(stored, 10);
  return isNaN(id) ? null : id;
}

/**
 * Check if the current user is a beta tester
 */
export function isBetaUser(): boolean {
  const resourceId = getCurrentResourceId();
  if (!resourceId) return false;

  // Also check for manual beta opt-in (for testing)
  const manualBeta = localStorage.getItem('autotask-hub-beta-enabled');
  if (manualBeta === 'true') return true;

  return BETA_USER_RESOURCE_IDS.includes(resourceId);
}

/**
 * Check if a specific feature is enabled for the current user
 */
export function isFeatureEnabled(feature: FeatureFlag): boolean {
  // Check if feature is in beta list
  if (BETA_FEATURES.includes(feature)) {
    return isBetaUser();
  }

  // Feature is generally available
  return true;
}

/**
 * Enable beta access manually (for testing)
 * Run in browser console: enableBetaAccess()
 */
export function enableBetaAccess(): void {
  localStorage.setItem('autotask-hub-beta-enabled', 'true');
  console.log('Beta access enabled! Refresh the page to see new features.');
}

/**
 * Disable beta access
 */
export function disableBetaAccess(): void {
  localStorage.removeItem('autotask-hub-beta-enabled');
  console.log('Beta access disabled. Refresh the page.');
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).enableBetaAccess = enableBetaAccess;
  (window as any).disableBetaAccess = disableBetaAccess;
  (window as any).isBetaUser = isBetaUser;
}
